<template>
  <div class="relative">
    <div class="h-full bg-white flex">
      <div class="min-w-0 flex-1">
        <div class="h-full flex relative">
          <div class="w-full h-full p-4 flex flex-col overflow-y-auto lg:p-8">
            <div class="flex flex-col gap-y-6">
              <div class="flex flex-col gap-y-2">
                <p class="w-max border border-blue-400 rounded-xl px-3 bg-white text-sm text-blue-400 leading-6">{{ $t("exam.question.title") }}</p>
                <p class="text-xl font-bold">{{ bankExam.examTitle }}</p>
              </div>
              <div class="flex flex-col gap-y-2">
                <div class="flex items-center gap-x-2">
                  <p class="w-max border border-blue-400 rounded-xl px-3 bg-white text-sm text-blue-400 leading-6">{{ $t("exam.question.number") }}</p>
                  <p class="text-sm break-words">{{ bankExam.questionList?.length }}개</p>
                </div>
                <div class="flex items-center gap-x-6">
                  <div class="flex items-center gap-x-2">
                    <p class="w-max border border-blue-400 rounded-xl px-2 bg-blue-400 text-sm text-white leading-6">{{ $t("exam.question.choice") }}</p>
                    <p class="text-sm break-words">{{ $t("exam.question.amount", { '0' : bankExam.questionList?.filter(question => question.questionType === 'CHOICE').length }) }}</p>
                  </div>
                  <div class="flex items-center gap-x-2">
                    <p class="w-max border border-blue-400 rounded-xl px-2 bg-blue-400 text-sm text-white leading-6">{{ $t("exam.question.subjective") }}</p>
                    <p class="text-sm break-words">{{ $t("exam.question.amount", { '0' : bankExam.questionList?.filter(question => question.questionType === 'SUBJECTIVE').length }) }}</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <TestbankResultCorrectTable />
            </div>
            <div class="min-w-0 flex-1"
                 :class="{
                   'opacity-50 pointer-events-none': !hasEmceeRole() && roomExamUsersAnswerObj[me.userId]?.completed,
                 }"
            >
              <component
                v-for="(question, i) in bankExam.questionList"
                :is="question.questionType === 'CHOICE' ? 'RoomExamQuestionChoice' : 'RoomExamQuestionSubjective'"
                :key="question.questionId"
                :index="i"
                :question="question"
              />
            </div>
            <GrmBtn v-if="!hasEmceeRole()" size="md" class="mt-3" @click="onCompleteExam">
                {{ $t(`exam.submit.${roomExamUsersAnswerObj[me.userId]?.completed ? 'edit.title' : 'complete' }`) }}
            </GrmBtn>
          </div>
          <div v-if="!hasEmceeRole()" class="w-full p-1 flex justify-end items-center absolute top-0">
            <RoomExamTimer class="rounded-lg" />
          </div>
        </div>
      </div>
      <div v-if="hasEmceeRole()">
        <TestbankViewInfo class="h-full absolute top-0 bottom-0 transition-all lg:relative lg:top-auto lg:bottom-auto lg:right-auto"
                          :class="[
                            isTestbankViewInfoOpen ? 'right-0' : '-right-64',
                          ]"
        />
        <div class="flex justify-center items-center absolute inset-0 pointer-events-none lg:hidden">
          <div class="w-4 h-7 border border-gray-300 bg-gray-200 rounded flex justify-center items-center absolute transition-all pointer-events-auto"
               :class="[
                  isTestbankViewInfoOpen ? 'right-60 transform translate-x-1/2' : 'right-0',
               ]"
               @click="onToggleTestbankViewInfo"
          >
            <GrmIcon :icon="`arrow-${isTestbankViewInfoOpen ? 'right' : 'left'}`" />
          </div>
        </div>
      </div>
    </div>
    <RoomExamLoading v-if="!bankExam.bankExamId" />
  </div>
</template>

<script>
import {mapActions, mapGetters, mapMutations, mapState} from "vuex";

import GrmBtn from "@/components/Common/button/GrmBtn.vue";
import GrmIcon from "@/components/Common/icon/GrmIcon.vue";
import GrmTextField from "@/components/Common/textField/GrmTextField.vue";
import RoomExamTimer from "@/components/Room/Testbank/View/RoomExamTimer.vue";
import RoomExamLoading from "@/components/Room/Testbank/View/RoomExamLoading.vue";
import TestbankViewInfo from '@/components/Room/Testbank/ViewInfo';
import RoomExamQuestionChoice from "@/components/Room/Exam/Question/RoomExamQuestionChoice.vue";
import RoomExamQuestionSubjective from "@/components/Room/Exam/Question/RoomExamQuestionSubjective.vue";
import TestbankResultCorrectTable from "@/components/Room/Testbank/Result/TestbankResultCorrectTable.vue";

export default {
  name: 'RoomExam',
  components: {
    GrmBtn,
    GrmIcon,
    GrmTextField,
    RoomExamTimer,
    RoomExamLoading,
    TestbankViewInfo,
    RoomExamQuestionChoice,
    RoomExamQuestionSubjective,
    TestbankResultCorrectTable,
  },
  data() {
    return {
      isTestbankViewInfoOpen: false,
    }
  },
  computed: {
    ...mapGetters('user', [
      'me',
      'hasEmceeRole',
    ]),
    ...mapState('exam', [
      'roomExam',
      'bankExam',
      'roomExamUsersAnswerObj'
    ]),
  },
  methods: {
    ...mapActions('exam', [
      'completeUserExam',
    ]),
    ...mapMutations('exam', [
      'EDIT_EXAM',
    ]),

    onCompleteExam() {
      const isEndedExam = new Date(this.roomExam.beginDate).getTime() + this.roomExam.examDuration * 1000 < new Date().getTime();
      if (isEndedExam) {
        this.$alert(this.$t('exam.submit.ended'));
        return;
      }

      if (this.roomExamUsersAnswerObj[this.me.userId].completed) {
        this.$alert({
          title: this.$t('exam.submit.edit.title'),
          text: this.roomExam.examType === 'QUIZ' ? this.$t('exam.submit.edit.text.quiz') : this.$t('exam.submit.edit.text.survey'),
          ok: async () => {
            this.EDIT_EXAM();
          }
        })
        return;
      }

      this.$alert({
        title: this.$t('exam.submit.title'),
        text: this.$t('exam.submit.confirm'),
        ok: async () => {
          try {
            await this.completeUserExam();
            this.$alert(this.$t('exam.submit.success'));
          } catch(e) {
            this.$alert(this.$t('exam.submit.fail'));
          }
        }
      })
    },
    onToggleTestbankViewInfo() {
      this.isTestbankViewInfoOpen = !this.isTestbankViewInfoOpen;
    }
  }
}
</script>